import { useEffect, useState } from "react";
import {
  getPublicCommercialConfig,
  type PublicCommercialConfig,
} from "./commercial-config.functions";
import { DEFAULT_PRICING, DEFAULT_USAGE_LIMITS, type PricingMap } from "./commercial-config";
import { loadCommercialConfig, type ConfigLoadState } from "./commercial-config-load";

export type { PricingEntry, PricingMap, UsageLimits } from "./commercial-config";

const DEFAULT_CONFIG: PublicCommercialConfig = {
  pricing: DEFAULT_PRICING,
  limits: DEFAULT_USAGE_LIMITS,
  // Sem resposta do servidor não há garantia de ambiente coerente: mostramos
  // os textos por omissão mas nunca autorizamos uma compra.
  paymentsAvailable: false,
  paymentsBlockedReason: "Configuração de pagamentos por confirmar.",
};

let cache: { value: PublicCommercialConfig; savedAt: number } | null = null;
let inflight: Promise<PublicCommercialConfig> | null = null;
const CACHE_TTL_MS = 60_000;

export async function fetchCommercialConfig(force = false): Promise<PublicCommercialConfig> {
  if (!force && cache && Date.now() - cache.savedAt < CACHE_TTL_MS) return cache.value;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const value = await getPublicCommercialConfig();
      cache = { value, savedAt: Date.now() };
      return value;
    } catch {
      return cache?.value ?? DEFAULT_CONFIG;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export async function fetchPricingLabels(): Promise<PricingMap> {
  return (await fetchCommercialConfig()).pricing;
}

export function invalidatePricingLabels() {
  cache = null;
}

export function useCommercialConfig(): PublicCommercialConfig {
  const [config, setConfig] = useState<PublicCommercialConfig>(
    () => cache?.value ?? DEFAULT_CONFIG,
  );
  useEffect(() => {
    let cancelled = false;
    const refresh = (force = false) => {
      fetchCommercialConfig(force).then((next) => {
        if (!cancelled) setConfig(next);
      });
    };
    refresh();
    const onFocus = () => refresh(true);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
    };
  }, []);
  return config;
}

export function usePricingLabels(): PricingMap {
  return useCommercialConfig().pricing;
}

/**
 * Disponibilidade de compras confirmada pelo servidor. Enquanto o ambiente
 * estiver incoerente (ex.: Live num host de Preview), os botões de compra
 * ficam desactivados — o resto da Routify continua operacional.
 */
export function usePaymentsAvailability(): { available: boolean; reason: string | null } {
  const config = useCommercialConfig();
  return {
    available: config.paymentsAvailable !== false,
    reason: config.paymentsAvailable === false ? config.paymentsBlockedReason : null,
  };
}

/**
 * Status-aware loader for pages that must never render empty nor hang:
 * skeleton while loading, controlled timeout, one retry, explicit error with a
 * manual "Tentar novamente", and the last valid config only as visual fallback.
 */
export function useCommercialConfigLoad(
  requiredKeys: string[] = [],
): ConfigLoadState & { reload: () => void } {
  const [state, setState] = useState<ConfigLoadState>({
    status: "loading",
    config: cache?.value ?? null,
  });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ status: "loading", config: prev.config ?? cache?.value ?? null }));
    loadCommercialConfig({
      fetcher: () => getPublicCommercialConfig(),
      requiredKeys,
      fallback: cache?.value ?? null,
    }).then((next) => {
      if (cancelled) return;
      if (next.status === "ready" && next.config) {
        cache = { value: next.config as PublicCommercialConfig, savedAt: Date.now() };
      }
      setState(next);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, requiredKeys.join(",")]);

  return { ...state, reload: () => setNonce((n) => n + 1) };
}
